import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type { KitAssetParams } from '../../../redux/api/fymAPI.types';
import type { PostMediaKind } from './platformMeta';

/**
 * The handoffs between Promote screens, held in the tab.
 *
 * The media that rides with a Composer post: a file that a Promote screen handed over ("Post it"),
 * a file picked in the Composer media picker, or an image the user uploaded there. A handover lives
 * in the tab only: it does not survive a reload.
 */
export interface PromoteMediaDraft {
  /** The FileModel id the post is created with (`mediaFileId`). */
  fileId: number;
  /** Absolute url of the file. */
  fileUrl: string;
  thumbnailUrl?: string;
  /**
   * Image or video. The server reads the kind from the MIME type of the file; this copy drives the
   * preview and the per-channel checks. Absent on an older handover: a video.
   */
  kind?: PostMediaKind;
  /** The shape, shown next to the media ("9:16", "4:5", or the pixel size). Empty when not known. */
  aspect: string;
  /** Pixels; null or absent when not known. */
  width?: number | null;
  height?: number | null;
  /** How long it runs, in seconds; null for an image. */
  durationSec: number | null;
  /** Bytes, when known (an uploaded video). The video rules of each platform read it. */
  sizeBytes?: number | null;
  /** The name of the asset ("Story (Out now)", "AI reel 30s", the uploaded file name). */
  label?: string;
  /** The release the media was made from, for the caption writer. */
  title: string;
  artistName: string;
  catalogItemUuid?: string;
  smartLinkId?: number;
  releaseDate?: string;
  /** When it was made. */
  renderedAt: string;
  /**
   * The asset type of a kit file (`StudioThemeId`, `aiReel` for a reel). A full-song video goes to
   * YouTube, Facebook and Instagram as a one-off publish, not a Calendar post (fym-shared
   * `calendarPlatformsReplacedForTheme`), so the Composer does not offer those three for it.
   * Absent for an upload.
   */
  themeId?: string;
  /**
   * The channels to pick (design rollout-plan c1, "Open in Composer" of a step: the channels of the
   * step). The Composer picks the usable ones that can take the media. Absent: its own rule.
   */
  platforms?: string[];
  /** The format of the post (a rollout story step opens the Composer on a story). Absent: a feed post. */
  format?: 'post' | 'story';
  /**
   * True when the file carries the Music24 watermark (a file of Free or the trial). The Composer
   * then shows the warning of design trial-upsell a5. Absent for an upload.
   */
  watermark?: boolean;
}

/** The kind of the media; a handover with no kind is a video. */
export function mediaKindOfDraft(media: PromoteMediaDraft): PostMediaKind {
  return media.kind ?? 'video';
}

let currentDraft: PromoteMediaDraft | undefined;

export function setPromoteMediaDraft(draft: PromoteMediaDraft): void {
  currentDraft = draft;
}

export function getPromoteMediaDraft(): PromoteMediaDraft | undefined {
  return currentDraft;
}

/** The input of an asset version on its way to the Studio ("New version" on the song page). */
export interface PendingStudioParams {
  catalogItemUuid: string;
  themeId: StudioThemeId;
  params: KitAssetParams;
}

let pendingStudioParams: PendingStudioParams | undefined;

/** The Assets tab's "New version": the Studio opens on the theme with these details. */
export function setPendingStudioParams(pending: PendingStudioParams): void {
  pendingStudioParams = pending;
}

/** Reads the pending Studio input and clears it, so it is only ever applied once. */
export function takePendingStudioParams(): PendingStudioParams | undefined {
  const pending = pendingStudioParams;
  pendingStudioParams = undefined;
  return pending;
}
